import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import { Sparkles, Zap, ShieldCheck, ArrowRight, HardDrive, Infinity as InfinityIcon } from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  ok:     'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  warn:   'bg-amber-500/10 text-amber-600 border-amber-500/40',
  capped: 'bg-violet-500/10 text-violet-500 border-violet-500/40',
};

function fmtDollars(n) {
  if (n == null) return '$0.00';
  return `$${Number(n).toFixed(n < 1 ? 2 : 2)}`;
}

function fmtCredits(n) {
  if (n == null) return '0';
  return Number(n).toLocaleString();
}

export default function HeaderLimitPill() {
  const { currentWorkspace } = useWorkspace();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    try {
      const url = currentWorkspace?.workspace_id
        ? `${API_URL}/api/billing/ai-usage?workspace_id=${currentWorkspace.workspace_id}`
        : `${API_URL}/api/billing/ai-usage`;
      const res = await apiFetch(url);
      if (res.ok) setUsage(await res.json());
    } catch (_e) { /* silent */ }
    setLoading(false);
  }, [currentWorkspace?.workspace_id]);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);
  // Re-fetch every 60s
  useEffect(() => {
    const t = setInterval(fetchUsage, 60_000);
    return () => clearInterval(t);
  }, [fetchUsage]);

  if (!usage) return null;

  const isFreeTier = usage.plan_id === 'free';
  const isCapped   = usage.ai_status === 'capped';
  const isWarn     = usage.ai_status === 'warn';
  const pct        = Math.min(100, usage.ai_pct_used || 0);
  const tone       = isFreeTier ? 'capped' : usage.ai_status || 'ok';
  const pillStyle  = STATUS_STYLES[tone];

  // Prefer credits display when the backend has the credit-ratio set
  // (server is on the new contract). Fall back to dollars otherwise.
  const hasCredits = typeof usage.credit_ratio_per_usd === 'number';
  const creditsRemaining = usage.credits_remaining;
  const creditsCap = usage.credits_effective_cap;
  const creditsSpent = usage.credits_spent_mtd;

  const label = isFreeTier
    ? `Free · ${usage.free_ai_used_today}/${usage.free_ai_daily_cap}`
    : hasCredits
      ? `${fmtCredits(creditsRemaining)} credits`
      : `${fmtDollars(usage.ai_spend_mtd_usd)} / ${fmtDollars(usage.ai_monthly_cap_usd)}`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="header-limit-pill"
          className={cn(
            'hidden md:inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
            'hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-emerald-500/40',
            pillStyle,
          )}
          aria-label="AI usage and limits"
        >
          {isFreeTier ? <InfinityIcon className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
          <span>AI: {label}</span>
          {!isFreeTier && hasCredits && !isCapped && (
            <span className="hidden lg:inline text-[10px] opacity-70">
              / {fmtCredits(creditsCap)}
            </span>
          )}
          {isCapped && !isFreeTier && (
            <Badge data-testid="pill-fallback-badge" className="ml-1 bg-violet-500/15 text-violet-500 text-[10px] py-0 px-1.5">
              Free fallback
            </Badge>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0 overflow-hidden" data-testid="header-limit-popover">
        {/* Header */}
        <div className="px-4 py-3 border-b bg-muted/40 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Your plan</p>
            <p className="font-semibold text-sm flex items-center gap-1.5">
              {usage.plan_name}
              <span className="text-muted-foreground font-normal">·</span>
              <span className="text-muted-foreground font-normal">${usage.plan_price}/mo</span>
            </p>
          </div>
          <Badge variant="outline" className={cn('text-[10px]', pillStyle)}>
            {isFreeTier ? 'Free tier' : isCapped ? 'Cap reached' : isWarn ? 'Approaching cap' : 'Healthy'}
          </Badge>
        </div>

        {/* AI premium usage */}
        <div className="px-4 py-3 space-y-3">
          <div>
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span className="inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Premium AI this month
              </span>
              <span>{Math.round(pct)}%</span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  isCapped ? 'bg-violet-500' : isWarn ? 'bg-amber-500' : 'bg-emerald-500',
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="flex justify-between text-[11px] text-muted-foreground mt-1" data-testid="pill-ai-spend">
              {hasCredits ? (
                <>
                  <span>{fmtCredits(creditsSpent)} credits used <span className="opacity-60">({fmtDollars(usage.ai_spend_mtd_usd)})</span></span>
                  <span>{fmtCredits(creditsRemaining)} of {fmtCredits(creditsCap)} left</span>
                </>
              ) : (
                <>
                  <span>{fmtDollars(usage.ai_spend_mtd_usd)} used</span>
                  <span>{fmtDollars(usage.ai_remaining_usd)} left of {fmtDollars(usage.ai_monthly_cap_usd)}</span>
                </>
              )}
            </div>
          </div>

          {/* Fallback reassurance message — explicit user requirement */}
          <div
            data-testid="pill-fallback-callout"
            className={cn(
              'rounded-lg border px-3 py-2 text-[11px] leading-relaxed flex items-start gap-2',
              isCapped || isFreeTier
                ? 'border-violet-500/30 bg-violet-500/5 text-violet-700 dark:text-violet-300'
                : 'border-emerald-500/20 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300',
            )}
          >
            {isCapped || isFreeTier ? (
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            ) : (
              <Zap className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            )}
            <span>
              {isCapped ? (
                <>You’ve hit your premium AI cap. CountBucks has automatically switched you to our <strong>free Llama 3.3 model</strong>, so you can keep working at no extra cost. Need premium back instantly? Top-up below.</>
              ) : isFreeTier ? (
                <>You’re on the <strong>Free</strong> plan — keep using unlimited Llama 3.3 questions every day. Premium models (Claude, GPT-4o) unlock starting at $19.99/mo.</>
              ) : hasCredits ? (
                <>Don’t worry — if you ever run out of credits, we automatically fall back to the free <strong>Llama 3.3 model</strong> so you can keep working. Top up any time for instant premium access.</>
              ) : (
                <>Don’t worry — if you ever exceed your <strong>{fmtDollars(usage.ai_monthly_cap_usd)} premium cap</strong>, we automatically fall back to the free Llama 3.3 model so you can keep working.</>
              )}
            </span>
          </div>

          {/* Free-tier daily counter (always visible — important for free users) */}
          <div className="flex items-center justify-between text-[11px] text-muted-foreground" data-testid="pill-free-counter">
            <span className="inline-flex items-center gap-1">
              <InfinityIcon className="w-3 h-3" /> Free Llama questions today
            </span>
            <span className="font-medium text-foreground">
              {usage.free_ai_used_today} / {usage.free_ai_daily_cap > 900 ? 'Unlimited' : usage.free_ai_daily_cap}
            </span>
          </div>

          {/* Storage */}
          {usage.storage_limit_gb > 0 && (
            <div className="flex items-center justify-between text-[11px] text-muted-foreground" data-testid="pill-storage">
              <span className="inline-flex items-center gap-1">
                <HardDrive className="w-3 h-3" /> Storage
              </span>
              <span className="font-medium text-foreground">
                {usage.storage_used_gb} / {usage.storage_limit_gb} GB
              </span>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="px-4 py-3 border-t bg-muted/30 flex items-center justify-between gap-2">
          <Link to="/settings?tab=billing" className="flex-1">
            <Button
              size="sm"
              variant="default"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
              data-testid="pill-upgrade-btn"
            >
              {isFreeTier ? 'Pick a plan' : isCapped ? 'Buy credit top-up' : 'Manage credits'}
              <ArrowRight className="w-3 h-3" />
            </Button>
          </Link>
          {!isFreeTier && (
            <Link to="/settings?tab=billing">
              <Button size="sm" variant="outline" className="text-xs" data-testid="pill-auto-topup-btn">
                Auto top-up
              </Button>
            </Link>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
